import type { ThreekitEmbedHandle } from "./embed-api";
import iconTilt from "./assets/icons/Tilt_Icon.png";
import iconLift from "./assets/icons/Elevate_Icon.png";
import iconRecline from "./assets/icons/Recline_Icon.png";
import iconArticulatingFootPlatform from "./assets/icons/Articulating_Foot_Platform_Icon.png";
import iconDimensions from "./assets/icons/Dimensions_Toggle_Icon.png";
import iconDoor from "./assets/icons/Door_Frame_Toggle_Icon.png";
import iconDoorSize from "./assets/icons/Door_Size_Toggle_Icon.png";

const STYLE_ID = "tk-anim-dock-styles";

function ensureAnimationDockStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.tk-anim-dock {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 20;
  pointer-events: auto;
}
.tk-anim-dock__btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #55606b;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.15s;
}
.tk-anim-dock__btn:hover {
  background: #47515a;
}
.tk-anim-dock__btn:focus-visible {
  outline: 2px solid #1a56db;
  outline-offset: 2px;
}
.tk-anim-dock__btn--on {
  background: #1a56db;
}
.tk-anim-dock__btn--on:hover {
  background: #1547b8;
}
.tk-anim-dock__icon {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}
.tk-anim-dock__subgroup {
  display: none;
  flex-direction: row;
  gap: 8px;
}
.tk-anim-dock__subgroup--visible {
  display: flex;
}
`;
  document.head.appendChild(style);
}

export interface AnimationToggleDockOptions {
  /**
   * Threekit attribute keys (default: Animation, Animation_2, Animation_3, Animation_4,
   * Dimensions, Doorway, "Door width")
   */
  attributes?: {
    fullSeatRecline?: string;
    raiseSeat?: string;
    reclineBackRest?: string;
    articulatingFootPlatform?: string;
    dimensions?: string;
    doorway?: string;
    doorWidth?: string;
  };
  /** If true, sends `"TRUE"` / `"FALSE"` strings instead of JSON booleans */
  useStringBooleans?: boolean;
}

export interface AnimationToggleDockHandle {
  detach(): void;
}

const DEFAULT_ATTRS = {
  fullSeatRecline: "Animation",
  raiseSeat: "Animation_2",
  reclineBackRest: "Animation_3",
  articulatingFootPlatform: "Animation_4",
  dimensions: "Dimensions",
  doorway: "Doorway",
  doorWidth: "Door width",
} as const;

const DOOR_WIDTH_VALUES = ['32"', '36"'] as const;

function boolValue(on: boolean, useStrings: boolean): boolean | string {
  if (useStrings) return on ? "TRUE" : "FALSE";
  return on;
}

/**
 * Overlays toggle controls on the left side of the player shell for seat animations,
 * dimensions, and doorway visibility (each a boolean Threekit attribute, TRUE / FALSE in
 * the catalog sense). When "Doorway" is toggled on, a single "Door width" toggle button also
 * appears (a string Threekit attribute): off sets 32", on sets 36".
 */
export function attachAnimationToggleDock(
  embed: ThreekitEmbedHandle,
  shell: HTMLElement,
  options: AnimationToggleDockOptions = {}
): AnimationToggleDockHandle {
  const attrs = {
    fullSeatRecline:
      options.attributes?.fullSeatRecline ?? DEFAULT_ATTRS.fullSeatRecline,
    raiseSeat: options.attributes?.raiseSeat ?? DEFAULT_ATTRS.raiseSeat,
    reclineBackRest:
      options.attributes?.reclineBackRest ?? DEFAULT_ATTRS.reclineBackRest,
    articulatingFootPlatform:
      options.attributes?.articulatingFootPlatform ??
      DEFAULT_ATTRS.articulatingFootPlatform,
    dimensions: options.attributes?.dimensions ?? DEFAULT_ATTRS.dimensions,
    doorway: options.attributes?.doorway ?? DEFAULT_ATTRS.doorway,
    doorWidth: options.attributes?.doorWidth ?? DEFAULT_ATTRS.doorWidth,
  };
  const useStrings = options.useStringBooleans === true;

  ensureAnimationDockStyles();

  type ToggleKey =
    | "fullSeatRecline"
    | "raiseSeat"
    | "reclineBackRest"
    | "articulatingFootPlatform"
    | "dimensions"
    | "doorway";

  const state: Record<ToggleKey, boolean> = {
    fullSeatRecline: false,
    raiseSeat: false,
    reclineBackRest: false,
    articulatingFootPlatform: false,
    dimensions: false,
    doorway: false,
  };

  if (getComputedStyle(shell).position === "static") {
    shell.style.position = "relative";
  }

  const dock = document.createElement("aside");
  dock.className = "tk-anim-dock";
  dock.setAttribute("aria-label", "Viewer overlay controls");

  const rows: {
    key: ToggleKey;
    label: string;
    title: string;
    icon: string;
  }[] = [
    {
      key: "fullSeatRecline",
      label: "Full seat recline",
      title: "Full seat recline (toggle)",
      icon: iconTilt,
    },
    {
      key: "raiseSeat",
      label: "Raise seat",
      title: "Raise seat (toggle)",
      icon: iconLift,
    },
    {
      key: "reclineBackRest",
      label: "Recline back rest",
      title: "Recline back rest (toggle)",
      icon: iconRecline,
    },
    {
      key: "articulatingFootPlatform",
      label: "Articulating foot platform",
      title: "Articulating foot platform (toggle)",
      icon: iconArticulatingFootPlatform,
    },
    {
      key: "dimensions",
      label: "Show dimensions",
      title: "Show dimensions (toggle)",
      icon: iconDimensions,
    },
    {
      key: "doorway",
      label: "Show doorway",
      title: "Show doorway (toggle)",
      icon: iconDoor,
    },
  ];

  function applyAttr(key: ToggleKey, on: boolean): Promise<unknown> {
    const patch: Record<string, unknown> = {
      [attrs[key]]: boolValue(on, useStrings),
    };
    return embed.setConfiguration(patch);
  }

  function syncButton(btn: HTMLButtonElement, key: ToggleKey): void {
    const on = state[key];
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.classList.toggle("tk-anim-dock__btn--on", on);
  }

  function syncDoorWidthVisibility(): void {
    doorWidthWrap.classList.toggle(
      "tk-anim-dock__subgroup--visible",
      state.doorway
    );
  }

  for (const row of rows) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tk-anim-dock__btn";
    btn.title = row.title;
    btn.setAttribute("aria-label", row.label);
    btn.innerHTML = `<img class="tk-anim-dock__icon" src="${row.icon}" alt="" aria-hidden="true" />`;
    syncButton(btn, row.key);
    btn.addEventListener("click", () => {
      const key = row.key;
      state[key] = !state[key];
      syncButton(btn, key);
      if (key === "doorway") syncDoorWidthVisibility();
      void applyAttr(key, state[key]).catch((err: unknown) => {
        console.error("[animation-toggle-dock]", row.label, err);
        state[key] = !state[key];
        syncButton(btn, key);
        if (key === "doorway") syncDoorWidthVisibility();
      });
    });
    dock.appendChild(btn);
  }

  const doorWidthWrap = document.createElement("div");
  doorWidthWrap.className = "tk-anim-dock__subgroup";

  /** Single toggle: off -> 32", on -> 36". */
  let doorWidthOn = false;
  function doorWidthValueFor(on: boolean): (typeof DOOR_WIDTH_VALUES)[number] {
    return on ? DOOR_WIDTH_VALUES[1] : DOOR_WIDTH_VALUES[0];
  }

  const doorWidthBtn = document.createElement("button");
  doorWidthBtn.type = "button";
  doorWidthBtn.className = "tk-anim-dock__btn";
  doorWidthBtn.innerHTML = `<img class="tk-anim-dock__icon" src="${iconDoorSize}" alt="" aria-hidden="true" />`;

  function syncDoorWidthButton(): void {
    const value = doorWidthValueFor(doorWidthOn);
    doorWidthBtn.title = `Door width: ${value} (toggle)`;
    doorWidthBtn.setAttribute("aria-label", `Door width: ${value}`);
    doorWidthBtn.setAttribute("aria-pressed", doorWidthOn ? "true" : "false");
    doorWidthBtn.classList.toggle("tk-anim-dock__btn--on", doorWidthOn);
  }
  syncDoorWidthButton();

  doorWidthBtn.addEventListener("click", () => {
    doorWidthOn = !doorWidthOn;
    syncDoorWidthButton();
    const value = doorWidthValueFor(doorWidthOn);
    void embed
      .setConfiguration({ [attrs.doorWidth]: value })
      .catch((err: unknown) => {
        console.error("[animation-toggle-dock] Door width", err);
        doorWidthOn = !doorWidthOn;
        syncDoorWidthButton();
      });
  });

  doorWidthWrap.appendChild(doorWidthBtn);
  syncDoorWidthVisibility();
  dock.appendChild(doorWidthWrap);

  shell.appendChild(dock);

  return {
    detach() {
      dock.remove();
    },
  };
}

